<template>
  <div ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('shared.edit_details') }}</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">{{ $t('shared.name') }}</label>
            <input v-model="form.name" type="text" class="form-control" maxlength="255" />
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('shared.description') }}</label>
            <textarea v-model="form.description" class="form-control" rows="4"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('shared.difficulty') }}</label>
            <select v-model="form.difficulty" class="form-select">
              <option v-for="opt in DIFFICULTY_OPTIONS" :key="opt" :value="opt">{{ difficultyLabel(opt) }}</option>
            </select>
          </div>

          <div class="mb-3">
            <p><label class="mb-1"><small><strong>{{ $t('shared.who_can_view') }}</strong></small></label></p>
            <div class="input-group text-muted">
              <p class="p-2 bg-dark w-100 rounded-pill">
                <select
                  class="custom-select text-muted w-100"
                  id="editDetailsPublic"
                  :value="form.publiclyVisible ? '1' : '2'"
                  @change="privacyChanged"
                  :disabled="!isEmailConfirmed"
                >
                  <template v-if="isEmailConfirmed">
                    <option value="1">{{ $t('shared.everyone_can_view') }}</option>
                    <option value="2">{{ $t('shared.only_i_can_view') }}</option>
                  </template>
                  <template v-else>
                    <option value="2">{{ $t('shared.only_i_can_view') }}</option>
                  </template>
                </select>
              </p>
            </div>
            <div v-if="!isEmailConfirmed" class="card p-2 mt-2">
              <small class="d-block">
                {{ $t('hikes.edit_hike_details_modal.you_must_confirm_your_email_to') }}
              </small>
              <button
                class="btn btn-link p-0"
                type="button"
                @click.prevent="resendConfirmationEmail"
                :disabled="resendConfirmationLoading"
              >
                <small>{{ resendConfirmationLoading ? $t('hikes.edit_hike_details_modal.sending') : $t('hikes.edit_hike_details_modal.resend_confirmation_email') }}</small>
              </button>
              <small v-if="resendConfirmationStatus" class="d-block text-muted">{{ resendConfirmationStatus }}</small>
            </div>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">{{ errorMessage }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('shared.cancel_2') }}</button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('shared.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { Modal } from 'bootstrap'
import { difficultyLabel } from '../../../composables/useHikeFilters'
import { i18n } from '../../../i18n'

const t = (...a) => i18n.global.t(...a)

// Values are the backend's difficulty strings; only the label is translated.
const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Difficult', 'Extra Difficult']
const props = defineProps({
  hike: { type: Object, default: null }
})

const emit = defineEmits(['save'])

const modalEl = ref(null)
let bsModal = null
const saving = ref(false)
const errorMessage = ref('')
const isEmailConfirmed = !!(window.user && window.user.confirmed_at)
const resendConfirmationLoading = ref(false)
const resendConfirmationStatus = ref('')

const form = reactive({
  name: '',
  description: '',
  difficulty: 'Medium',
  publiclyVisible: true
})

watch(() => props.hike, (hike) => {
  if (hike) {
    form.name = hike.name || ''
    form.description = hike.description || ''
    form.difficulty = hike.difficulty || 'Medium'
    form.publiclyVisible = isEmailConfirmed ? (hike.publiclyVisible !== false) : false
    errorMessage.value = ''
  }
}, { immediate: true })

onMounted(() => {
  bsModal = new Modal(modalEl.value)
})

onBeforeUnmount(() => {
  bsModal?.dispose()
})

function privacyChanged (event) {
  if (!isEmailConfirmed) {
    form.publiclyVisible = false
    return
  }
  form.publiclyVisible = event.target.value === '1'
}

async function resendConfirmationEmail () {
  if (resendConfirmationLoading.value) return
  resendConfirmationLoading.value = true
  resendConfirmationStatus.value = ''
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch('/user/resend_confirmation', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' }
    })
    const data = await response.json()
    resendConfirmationStatus.value = data?.message || t('hikes.edit_hike_details_modal.confirmation_email_sent')
  } catch {
    resendConfirmationStatus.value = t('hikes.edit_hike_details_modal.could_not_send_confirmation_email')
  } finally {
    resendConfirmationLoading.value = false
  }
}

function show () {
  errorMessage.value = ''
  saving.value = false
  bsModal?.show()
}

function hide () {
  bsModal?.hide()
}

async function save () {
  if (!form.name.trim()) {
    errorMessage.value = t('hikes.edit_hike_details_modal.name_is_required')
    return
  }
  saving.value = true
  errorMessage.value = ''
  emit('save', { ...form }, { done, fail })
}

function done () {
  saving.value = false
  hide()
}

function fail (msg) {
  saving.value = false
  errorMessage.value = msg || t('hikes.edit_hike_details_modal.something_went_wrong')
}

defineExpose({ show, hide })
</script>

<style scoped>
.modal-content {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
}

.modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.modal-footer {
  border-top-color: rgba(255, 255, 255, 0.08);
}

.form-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.3rem;
}

.form-control,
.form-select {
  background: #2a2b33;
  border-color: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.form-control:focus,
.form-select:focus {
  background: #2a2b33;
  border-color: #FBCD01;
  color: #fff;
  box-shadow: 0 0 0 0.2rem rgba(251, 205, 1, 0.15);
}

.form-select option {
  background: #2a2b33;
  color: #fff;
}

.form-check-input:checked {
  background-color: #FBCD01;
  border-color: #FBCD01;
}

.form-check-label {
  font-size: 0.9rem;
}

.btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>
